import React, {useMemo} from "react";
import {Row, Col, Collapse, Select, Radio, InputNumber, Button} from "antd";
import {
    CompanyFilter,
    SubCompanyFilter,
    CityFilter,
    PoolFilter,
    SubCompanyWorkTypeFilter,
    SubCompanyActivityFilter
} from "../../_defaultComponents/FilterComponents";
import {useTranslation} from "react-i18next";
import {useFetch} from "hooks/useFetch";
import {useStore} from "hooks/useStore";
import {isRealPool} from "utils/poolUtils";

// Dedike entegrasyon havuzlarındaki şubeler operasyonel olarak dedike sayılır,
// bu yüzden "Havuz" kovası yalnızca entegrasyon tipi "pool" olan havuzları içerir.
const DEDICATED_POOL_INTEGRATION_TYPES = ["dedicated", "dedicatedIntegration"];

// Eski havuz kayıtlarında alan boş olabiliyor; backend de bunları "pool" kabul ediyor
const getPoolIntegrationType = pool => pool?.integrationType || "pool";

export const DEFAULT_FILTERS = {
    subCompanyWorkType: undefined,
    city: undefined,
    poolId: undefined,
    companyId: undefined,
    subCompanyId: undefined,
    subCompanyActivity: "all",
    hasActiveContract: "yes",
    contractDocumentStatus: "all",
    selectedFeeType: undefined,
    courierAmountMin: undefined,
    courierAmountMax: undefined,
    clientAmountMin: undefined,
    clientAmountMax: undefined,
    courierMinimumDistanceMin: undefined,
    courierMinimumDistanceMax: undefined,
    clientMinimumDistanceMin: undefined,
    clientMinimumDistanceMax: undefined,
    courierGuaranteePackageAmountMin: undefined,
    courierGuaranteePackageAmountMax: undefined,
    clientGuaranteePackageAmountMin: undefined,
    clientGuaranteePackageAmountMax: undefined
};

export const SubCompanyPricingFilters = ({
    filters,
    onChange,
    onReset,
    isVisible,
    onVisibilityChange
}) => {
    const {t} = useTranslation(["subCompanyPricing"]);
    const stores = useStore();

    // Entegrasyon tipine göre daraltma yalnızca acente kullanıcısı için geçerli
    const isAgency = stores.meStore.checkRole("isAgency");
    const isPoolWorkType = filters.subCompanyWorkType === "pool";

    // Havuzların entegrasyon tipi, alt firma listesini kovaya göre daraltmak için gerekli.
    // Şehir filtresi bilinçli olarak uygulanmıyor: tablo alt firmanın şehrine göre filtreleniyor,
    // havuz kaydının şehri ise farklı olabiliyor — şehre göre daraltmak eşleşmeyi bozuyor.
    const [, pools] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.poolStore.getPoolList.bind(null, {
            limit: 1000,
            page: 1
        }),
        condition: isAgency
    });

    const {poolIntegrationTypeBySubCompanyId, poolIntegrationCompanyIds} = useMemo(() => {
        const typeBySubCompanyId = new Map();
        const companyIds = new Set();

        (pools?.data || []).forEach(pool => {
            const integrationType = getPoolIntegrationType(pool);

            pool?.subCompanies?.forEach(subCompany => {
                typeBySubCompanyId.set(subCompany?._id?.toString(), integrationType);
                if (integrationType === "pool" && subCompany?.companyId) {
                    companyIds.add(subCompany.companyId.toString());
                }
            });
        });

        return {
            poolIntegrationTypeBySubCompanyId: typeBySubCompanyId,
            poolIntegrationCompanyIds: companyIds
        };
    }, [pools]);

    const subCompanyOptionFilter = subCompany => {
        const integrationType = poolIntegrationTypeBySubCompanyId.get(subCompany?._id?.toString());

        return isPoolWorkType
            ? subCompany?.subCompanyWorkType === "pool" && integrationType === "pool"
            : subCompany?.subCompanyWorkType === "dedicated" ||
                  DEDICATED_POOL_INTEGRATION_TYPES.includes(integrationType);
    };

    // Dedike kovasında sabit alt firmaların firmaları client-side listelenemediği için
    // firma listesi daraltılmaz; dedike entegrasyon firmaları zaten tam listede mevcut.
    const companyOptionFilter = company => poolIntegrationCompanyIds.has(company?._id?.toString());

    const updateFilter = (field, value) => {
        onChange({
            ...filters,
            [field]: value
        });
    };

    const updateMultipleFilters = updates => {
        onChange({
            ...filters,
            ...updates
        });
    };

    return (
        <Collapse
            activeKey={isVisible ? "1" : null}
            onChange={onVisibilityChange}
            style={{marginTop: "25px", marginBottom: "25px"}}
        >
            <Collapse.Panel header={t("filters", {ns: "subCompanyPricing"})} key="1">
                <Row gutter={[16, 16]}>
                    {/* Sol Taraf - Temel Filtreler */}
                    <Col xs={24} lg={12}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12}>
                                <SubCompanyWorkTypeFilter
                                    showLabel={true}
                                    marginBottom="0px"
                                    value={filters.subCompanyWorkType}
                                    onChange={value =>
                                        // Acentede kova değişince önceki havuz/firma/alt firma
                                        // seçimleri yeni kovada geçersiz kalabiliyor
                                        isAgency
                                            ? updateMultipleFilters({
                                                  subCompanyWorkType: value,
                                                  poolId: undefined,
                                                  companyId: undefined,
                                                  subCompanyId: undefined
                                              })
                                            : updateFilter("subCompanyWorkType", value)
                                    }
                                />
                            </Col>
                            <Col xs={24} sm={12}>
                                <CityFilter
                                    selectMarginBottom="0px"
                                    value={filters.city}
                                    onChange={value =>
                                        updateMultipleFilters({
                                            city: value,
                                            poolId: undefined
                                        })
                                    }
                                />
                            </Col>
                            <Col xs={24} sm={12}>
                                <PoolFilter
                                    marginBottom="0px"
                                    value={filters.poolId}
                                    allowClear={true}
                                    onChange={value => updateFilter("poolId", value)}
                                    city={filters.city}
                                    optionFilter={isAgency ? isRealPool : undefined}
                                />
                            </Col>
                            <Col xs={24} sm={12}>
                                <CompanyFilter
                                    selectMarginBottom="0px"
                                    value={filters.companyId}
                                    optionFilter={
                                        isAgency && isPoolWorkType ? companyOptionFilter : undefined
                                    }
                                    onChange={value =>
                                        updateMultipleFilters({
                                            companyId: value,
                                            subCompanyId: undefined
                                        })
                                    }
                                />
                            </Col>
                            <Col xs={24} sm={12}>
                                <h5>Alt Firma</h5>
                                <SubCompanyFilter
                                    selectMarginBottom="0px"
                                    hideTitle={true}
                                    value={filters.subCompanyId}
                                    onChange={value => updateFilter("subCompanyId", value)}
                                    company={filters.companyId}
                                    optionFilter={isAgency ? subCompanyOptionFilter : undefined}
                                />
                            </Col>
                            <Col xs={24} sm={12}>
                                <SubCompanyActivityFilter
                                    showLabel={true}
                                    selectMarginBottom="0px"
                                    value={filters.subCompanyActivity}
                                    onChange={value =>
                                        // Pasif alt firmalarda aktif sözleşme beklenmez;
                                        // seçimde "Aktif Sözleşme" filtresini "Tümü"ye çekiyoruz
                                        value === "disabled"
                                            ? updateMultipleFilters({
                                                  subCompanyActivity: value,
                                                  hasActiveContract: "all"
                                              })
                                            : updateFilter("subCompanyActivity", value)
                                    }
                                />
                            </Col>
                            <Col xs={24} sm={12}>
                                <h5>
                                    {t("hasActiveContract", {
                                        ns: "subCompanyPricing"
                                    })}
                                </h5>
                                <Select
                                    style={{width: "100%"}}
                                    value={filters.hasActiveContract}
                                    onChange={value => updateFilter("hasActiveContract", value)}
                                >
                                    <Select.Option value="all">
                                        {t("all", {ns: "subCompanyPricing"})}
                                    </Select.Option>
                                    <Select.Option value="yes">
                                        {t("yes", {ns: "subCompanyPricing"})}
                                    </Select.Option>
                                    <Select.Option value="no">
                                        {t("no", {ns: "subCompanyPricing"})}
                                    </Select.Option>
                                </Select>
                            </Col>
                            {filters.hasActiveContract === "yes" && (
                                <Col xs={24} sm={12}>
                                    <h5>{t("contractDocumentStatus", {
                                            ns: "subCompanyPricing"
                                        })} </h5>
                                    <Select
                                        style={{width: "100%"}}
                                        value={filters.contractDocumentStatus}
                                        onChange={value => updateFilter("contractDocumentStatus", value)}
                                    >
                                        <Select.Option value="all">
                                            {t("all", {ns: "subCompanyPricing"})}
                                        </Select.Option>
                                        <Select.Option value="uploaded">
                                            {t("uploaded", {ns: "subCompanyPricing"})}
                                        </Select.Option>
                                        <Select.Option value="notUploaded">
                                            {t("notUploaded", {ns: "subCompanyPricing"})}
                                        </Select.Option>
                                    </Select>
                                </Col>
                            )}
                        </Row>
                    </Col>

                    {/* Sağ Taraf - Ücret Filtreleri */}
                    <Col xs={24} sm={24} md={24} lg={18} xl={12}>
                        <h5>{"Sözleşme Türü"}</h5>
                        <Row gutter={[16, 8]}>
                            <Col xs={24}>
                                <Radio.Group
                                    value={filters.selectedFeeType}
                                    onChange={e => {
                                        const newFeeType = e.target.value;
                                        updateMultipleFilters({
                                            selectedFeeType: newFeeType,
                                            // Min-max değerlerini sıfırla
                                            courierAmountMin: undefined,
                                            courierAmountMax: undefined,
                                            clientAmountMin: undefined,
                                            clientAmountMax: undefined,
                                            courierMinimumDistanceMin: undefined,
                                            courierMinimumDistanceMax: undefined,
                                            clientMinimumDistanceMin: undefined,
                                            clientMinimumDistanceMax: undefined,
                                            courierGuaranteePackageAmountMin: undefined,
                                            courierGuaranteePackageAmountMax: undefined,
                                            clientGuaranteePackageAmountMin: undefined,
                                            clientGuaranteePackageAmountMax: undefined
                                        });
                                    }}
                                >
                                    <Radio value={undefined}>
                                        {t("all", {ns: "subCompanyPricing"})}
                                    </Radio>
                                    <Radio value="PAKET">
                                        {t("paket", {ns: "subCompanyPricing"})}
                                    </Radio>
                                    <Radio value="KM">
                                        {t("distance", {ns: "subCompanyPricing"})}
                                    </Radio>
                                    <Radio value="SAATLIK_UCRET">
                                        {t("hourly", {ns: "subCompanyPricing"})}
                                    </Radio>
                                    <Radio value="SAATLIK_GARANTI_PAKET">
                                        {t("guaranteedHourly", {ns: "subCompanyPricing"})}
                                    </Radio>
                                    <Radio value="BONUS_PAKET_KAZANCI_UCRETI">
                                        {t("bonusPackagePrice", {ns: "subCompanyPricing"})}
                                    </Radio>
                                </Radio.Group>
                            </Col>
                            {/* Dinamik Min-Max Filtreler - Seçilen ücret tipine göre gösterilir */}
                            {filters.selectedFeeType === "SAATLIK_GARANTI_PAKET" ? (
                                <>
                                    <Col xs={24} sm={24} md={13}>
                                        <h5>
                                            {t("courierPackageAmount", {ns: "subCompanyPricing"})}
                                        </h5>
                                        <div style={{display: "flex", gap: "8px"}}>
                                            <InputNumber
                                                placeholder={t("min", {ns: "subCompanyPricing"})}
                                                style={{width: "100%"}}
                                                value={filters.courierGuaranteePackageAmountMin}
                                                onChange={value =>
                                                    updateFilter(
                                                        "courierGuaranteePackageAmountMin",
                                                        value
                                                    )
                                                }
                                                min={0}
                                                allowClear={true}
                                            />
                                            <InputNumber
                                                placeholder={t("max", {ns: "subCompanyPricing"})}
                                                style={{width: "100%"}}
                                                value={filters.courierGuaranteePackageAmountMax}
                                                onChange={value =>
                                                    updateFilter(
                                                        "courierGuaranteePackageAmountMax",
                                                        value
                                                    )
                                                }
                                                min={0}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={24} md={13}>
                                        <h5>
                                            {t("clientPackageAmount", {ns: "subCompanyPricing"})}
                                        </h5>
                                        <div style={{display: "flex", gap: "8px"}}>
                                            <InputNumber
                                                placeholder={t("min", {ns: "subCompanyPricing"})}
                                                style={{width: "100%"}}
                                                value={filters.clientGuaranteePackageAmountMin}
                                                onChange={value =>
                                                    updateFilter(
                                                        "clientGuaranteePackageAmountMin",
                                                        value
                                                    )
                                                }
                                                min={0}
                                            />
                                            <InputNumber
                                                placeholder={t("max", {ns: "subCompanyPricing"})}
                                                style={{width: "100%"}}
                                                value={filters.clientGuaranteePackageAmountMax}
                                                onChange={value =>
                                                    updateFilter(
                                                        "clientGuaranteePackageAmountMax",
                                                        value
                                                    )
                                                }
                                                min={0}
                                            />
                                        </div>
                                    </Col>
                                </>
                            ) : (
                                <>
                                    {filters.selectedFeeType && (
                                        <>
                                            <Col xs={24} sm={24} md={13}>
                                                <h5>
                                                    {t("courierAmount", {ns: "subCompanyPricing"})}
                                                </h5>
                                                <div style={{display: "flex", gap: "8px"}}>
                                                    <InputNumber
                                                        placeholder={t("min", {
                                                            ns: "subCompanyPricing"
                                                        })}
                                                        style={{width: "100%"}}
                                                        value={filters.courierAmountMin}
                                                        onChange={value =>
                                                            updateFilter("courierAmountMin", value)
                                                        }
                                                        min={0}
                                                    />
                                                    <InputNumber
                                                        placeholder={t("max", {
                                                            ns: "subCompanyPricing"
                                                        })}
                                                        style={{width: "100%"}}
                                                        value={filters.courierAmountMax}
                                                        onChange={value =>
                                                            updateFilter("courierAmountMax", value)
                                                        }
                                                        min={0}
                                                    />
                                                </div>
                                            </Col>
                                            <Col xs={24} sm={24} md={13}>
                                                <h5>
                                                    {t("clientAmount", {ns: "subCompanyPricing"})}
                                                </h5>
                                                <div style={{display: "flex", gap: "8px"}}>
                                                    <InputNumber
                                                        placeholder={t("min", {
                                                            ns: "subCompanyPricing"
                                                        })}
                                                        style={{width: "100%"}}
                                                        value={filters.clientAmountMin}
                                                        onChange={value =>
                                                            updateFilter("clientAmountMin", value)
                                                        }
                                                        min={0}
                                                    />
                                                    <InputNumber
                                                        placeholder={t("max", {
                                                            ns: "subCompanyPricing"
                                                        })}
                                                        style={{width: "100%"}}
                                                        value={filters.clientAmountMax}
                                                        onChange={value =>
                                                            updateFilter("clientAmountMax", value)
                                                        }
                                                        min={0}
                                                    />
                                                </div>
                                            </Col>
                                        </>
                                    )}
                                    {filters.selectedFeeType === "KM" && (
                                        <Col xs={24} sm={24} md={13}>
                                            <h5>
                                                {t("courierMinimumDistance", {ns: "subCompanyPricing"})}
                                            </h5>
                                            <div style={{display: "flex", gap: "8px"}}>
                                                <InputNumber
                                                    placeholder={t("min", {
                                                        ns: "subCompanyPricing"
                                                    })}
                                                    style={{width: "100%"}}
                                                    value={filters.courierMinimumDistanceMin}
                                                    onChange={value =>
                                                        updateFilter("courierMinimumDistanceMin", value)
                                                    }
                                                    min={0}
                                                />
                                                <InputNumber
                                                    placeholder={t("max", {
                                                        ns: "subCompanyPricing"
                                                    })}
                                                    style={{width: "100%"}}
                                                    value={filters.courierMinimumDistanceMax}
                                                    onChange={value =>
                                                        updateFilter("courierMinimumDistanceMax", value)
                                                    }
                                                    min={0}
                                                />
                                            </div>
                                        </Col>
                                    )}
                                    {filters.selectedFeeType === "KM" && (
                                        <Col xs={24} sm={24} md={13}>
                                            <h5>
                                                {t("clientMinimumDistance", {ns: "subCompanyPricing"})}
                                            </h5>
                                            <div style={{display: "flex", gap: "8px"}}>
                                                <InputNumber
                                                    placeholder={t("min", {
                                                        ns: "subCompanyPricing"
                                                    })}
                                                    style={{width: "100%"}}
                                                    value={filters.clientMinimumDistanceMin}
                                                    onChange={value =>
                                                        updateFilter("clientMinimumDistanceMin", value)
                                                    }
                                                    min={0}
                                                />
                                                <InputNumber
                                                    placeholder={t("max", {
                                                        ns: "subCompanyPricing"
                                                    })}
                                                    style={{width: "100%"}}
                                                    value={filters.clientMinimumDistanceMax}
                                                    onChange={value =>
                                                        updateFilter("clientMinimumDistanceMax", value)
                                                    }
                                                    min={0}
                                                />
                                            </div>
                                        </Col>
                                    )}
                                </>
                            )}
                        </Row>
                    </Col>
                </Row>
                <Row justify="end">
                    <Button onClick={onReset} style={{marginTop: "16px"}}>
                        {t("resetFilters", {ns: "subCompanyPricing"})}
                    </Button>
                </Row>
            </Collapse.Panel>
        </Collapse>
    );
};
